/**
 * ===========================================================================
 * THE SIGNUP CHART, AS ARITHMETIC.
 * ===========================================================================
 *
 * No browser, no DOM, no React, and now that is true of the module as well as
 * of the functions. It lived inside `Observability.tsx`, so the test that
 * checks it had to import a React component to reach it, and importing a
 * component means resolving the JSX runtime.
 *
 * That worked on a machine with `admin/node_modules` sitting there from some
 * earlier install and failed in CI, which installs `app/` only and has no way
 * to install this tree because it has no package.json. The suite went green
 * locally and red on every push.
 *
 * The functions were always pure. Putting them where their own test said they
 * were is the fix, and it is the reason they are worth testing at all: an SVG
 * that divides by a zero maximum draws nothing and throws nothing, so a fresh
 * database renders a panel, an axis, and a row of bars that are all `NaN`.
 */

/** An even top just above the tallest bar, so the plot is not mostly headroom. */
export function ceiling(max: number): number {
  if (max <= 4) return 4;
  const step = Math.max(1, 10 ** Math.floor(Math.log10(max / 2)));
  return Math.ceil(max / (2 * step)) * 2 * step;
}

export function shorten(value: string): string {
  return value.length <= 10 ? value : `${value.slice(0, 8)}...`;
}

export const PLOT = { width: 560, height: 176, pad: 10, headroom: 16 } as const;

export type Bar = { readonly day: string; readonly x: number; readonly y: number; readonly tall: number };

/**
 * The whole chart as numbers, so an empty series, a single day and a flat week
 * can be checked without a browser. A NaN here draws nothing and throws nothing.
 */
export function plot(series: readonly { day: string; count: number }[]) {
  const { width, height, pad, headroom } = PLOT;
  const counts = series.map((one) => one.count);
  const top = ceiling(Math.max(...counts, 0));
  const slot = (width - 2 * pad) / Math.max(series.length, 1);
  const barWidth = Math.max(4, slot - 6);

  const bars: Bar[] = series.map((one, index) => {
    const tall = Math.round((one.count / top) * (height - headroom));
    return {
      day: one.day,
      x: pad + index * slot + (slot - barWidth) / 2,
      y: height - tall,
      tall,
    };
  });

  const tops = bars.map((one) => `${(one.x + barWidth / 2).toFixed(1)},${one.y.toFixed(1)}`);
  return {
    top,
    barWidth,
    bars,
    total: counts.reduce((sum, one) => sum + one, 0),
    line: tops.join(" "),
    area: `${tops.join(" ")} ${width - pad},${height} ${pad},${height}`,
  };
}
